// PoE 新攝影機待核准（SPEC #kinio-station-dhcp 第 2 種、NUI-01 的 VMS 端、W23）：NVR / Kinio Station 通道頁的「PoE 新攝影機」區塊
// 使用者點開時才每 5 秒問 `dhcp.pending`（op:"rpc"，同 App；收起或離開就停）——純瀏覽不打設備命令（B-10：每次問都會留一筆
// device.control 稽核）；網址帶 ?poe=1（通知的連結）就直接打開。有待核准的列就顯示卡片：MAC、廠商、要位址的次數與時間、上行網段嫌疑。
//   〔加入〕= dhcp.approve {mac} → 等攝影機拿到位址（同一列 state = approved 且帶 ip，最多 120 秒）→ 用 ONVIF 納管接進通道
//   （自家 Kinio 攝影機探索得到就照 E1 自己佔槽位、這裡只說明；第三方輸入帳密後 …/vms/onvif/devices）；
//   上行網段嫌疑（suspect ≠ none）不能一鍵加入，只有「強制加入」：要寫原因（≥ 5 字，寫進設備 syslog）；〔忽略〕= dhcp.ignore（24 小時不再提示）。
// 只給能管理這台的人（owner / admin；設備也擋）；這台沒有 kinio-dhcpd（方法不存在）就整塊不出現、不再問。
// 已核准（approved）的列不再出卡：白名單裡每台以前加過的攝影機都是 approved + ip，重新列出來會一直提示已經接好的攝影機；
// 核准後中斷（重新整理、別的端核准）的那台，用設備頁「新增 ONVIF 攝影機」探索得到（第三方、未納管）再接。
// data-testid：poe-approvals-block（可收合的區塊）、poe-approvals-toggle、poe-approvals、poe-none、poe-unsupported、poe-card（data-mac、data-suspect、data-step = new | approving | waiting_ip | got_ip | added | failed）、
// poe-approve、poe-force-reason、poe-force、poe-ignore、poe-user、poe-password、poe-add。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { KinioError } from '@kinio/web-sdk';
import { stationRpc } from '../../lib/deviceApi';
import { onvifApi } from '../../lib/planeApi';
import type { VmsDevice } from '../../lib/devices';
import { stamp } from '../../lib/timefmt';

export interface PendingItem {
  mac: string;
  oui_vendor?: string;
  first_seen?: string | number;
  last_seen?: string | number;
  count?: number;
  requested_ip?: string;
  suspect?: 'none' | 'uplink_ip' | 'uplink_arp' | string;
  state?: 'pending' | 'approved' | 'ignored' | string;
  ip?: string;
  port?: number;
}

export const POLL_MS = 5000;
export const WAIT_IP_MS = 120_000;
const NO_METHOD = new Set(['UNKNOWN_METHOD', 'METHOD_NOT_FOUND', 'NOT_SUPPORTED', 'UNSUPPORTED', 'NO_SUCH_METHOD', 'NOT_FOUND']);

/** NVR / Station 的本地 RPC：同 lib/deviceApi 的 stationRpc（現場設備分頁也用）。 */
export const gatewayRpc = stationRpc;

type Step = 'new' | 'approving' | 'waiting_ip' | 'got_ip' | 'added' | 'failed';

function ago(v: string | number | undefined, _lang: string): string {
  if (v === undefined) return '';
  const ms = typeof v === 'number' ? (v < 1e12 ? v * 1000 : v) : Date.parse(v);
  return Number.isFinite(ms) ? stamp(ms) : String(v);
}

function Card({ dev, item, onChanged, onActive }: { dev: VmsDevice; item: PendingItem; onChanged: () => void; onActive: (on: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('new');
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [ip, setIp] = useState<string | null>(null);
  const [own, setOwn] = useState(false);
  const [port, setPort] = useState(80);
  const [adding, setAdding] = useState(false);
  const [user, setUser] = useState('admin');
  const [password, setPassword] = useState('');
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  const suspect = !!item.suspect && item.suspect !== 'none';
  const errText = (e: unknown) => (e instanceof KinioError ? t(`poe.err_${e.code}`, { defaultValue: t('poe.err_default', { code: e.code }) }) : String(e));

  const waitForIp = useCallback(async () => {
    setStep('waiting_ip');
    const until = Date.now() + WAIT_IP_MS;
    while (alive.current && Date.now() < until) {
      try {
        const r = await gatewayRpc<{ items?: PendingItem[] }>(dev, 'dhcp.pending', {});
        const me = r?.items?.find((x) => x.mac === item.mac);
        if (me?.state === 'approved' && me.ip) {
          setIp(me.ip);
          setStep('got_ip');
          // 自家 Kinio 攝影機：探索得到（E1）就由 NVR 自己佔槽位、不要帳密
          try {
            const d = await onvifApi.discover(dev.apiEndpoint, dev.homeId, dev.aid);
            if ([...d.groups.own, ...d.groups.managed].some((c) => c.host === me.ip)) setOwn(true);
            const third = [...d.groups.third_party, ...d.groups.suspect].find((c) => c.host === me.ip);
            if (third?.port) setPort(third.port); // 探索回的 ONVIF 埠（不一定是 80）
          } catch {
            /* 探索失敗不擋：照第三方走 */
          }
          return;
        }
      } catch {
        /* 暫時問不到：下一輪再問 */
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    if (alive.current) {
      setErr(t('poe.no_ip'));
      setStep('failed'); // 卡片留著（已核准），可以再等一次或改走「新增 ONVIF 攝影機」
    }
  }, [dev, item.mac, t]);

  const approve = async (force: boolean) => {
    setErr(null);
    setStep('approving');
    onActive(true); // 核准後這一列不再是 pending：流程走完前卡片要留著
    try {
      await gatewayRpc(dev, 'dhcp.approve', force ? { mac: item.mac, force: true, reason: reason.trim() } : { mac: item.mac });
      await waitForIp();
    } catch (e) {
      setErr(errText(e));
      setStep('new');
      onActive(false);
    }
  };
  const ignore = async () => {
    setErr(null);
    try {
      await gatewayRpc(dev, 'dhcp.ignore', { mac: item.mac });
      onChanged();
    } catch (e) {
      setErr(errText(e));
    }
  };
  const add = async () => {
    if (!ip || adding) return;
    setErr(null);
    setAdding(true); // 送出中不能再按（不重複新增）
    try {
      const r = await onvifApi.add(dev.apiEndpoint, dev.homeId, { gateway: dev.aid, host: ip, port, user, password, name: item.oui_vendor });
      setPassword('');
      if (r.result !== 'ok') {
        setErr(t(`onvif.result_${r.result}`));
        return;
      }
      setStep('added');
      await qc.invalidateQueries({ queryKey: ['home'] });
      // 接好了：留著「已接進通道」幾秒讓人看到，之後這張卡收掉（不再是 pending，主輪詢下一輪就不列）
      setTimeout(() => onActive(false), 8000);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className={`card poe-card${suspect ? ' warn' : ''}`} data-testid="poe-card" data-mac={item.mac} data-suspect={suspect ? item.suspect : 'none'} data-step={step}>
      <div>
        <strong>{t('poe.new_camera')}</strong> <span className="mono">{item.mac}</span>
        {item.oui_vendor && <span className="muted"> · {item.oui_vendor}</span>}
        {item.port !== undefined && <span className="muted"> · {t('poe.port', { port: item.port })}</span>}
      </div>
      <div className="small muted">
        {t('poe.asked', { count: item.count ?? 1, first: ago(item.first_seen, i18n.language), last: ago(item.last_seen, i18n.language) })}
      </div>
      {suspect && <p className="small warn">{t(`poe.suspect_${item.suspect}`, { defaultValue: t('poe.suspect_other') })}</p>}
      {step === 'new' &&
        (suspect ? (
          <div className="toolbar wrap">
            <input className="input" placeholder={t('poe.force_reason')} aria-label={t('poe.force_reason')} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="poe-force-reason" />
            <button type="button" className="btn btn-danger" disabled={reason.trim().length < 5} onClick={() => void approve(true)} data-testid="poe-force">
              {t('poe.force')}
            </button>
            <button type="button" className="btn" onClick={() => void ignore()} data-testid="poe-ignore">
              {t('poe.ignore')}
            </button>
          </div>
        ) : (
          <div className="toolbar">
            <button type="button" className="btn btn-primary" onClick={() => void approve(false)} data-testid="poe-approve">
              {t('poe.approve')}
            </button>
            <button type="button" className="btn" onClick={() => void ignore()} data-testid="poe-ignore">
              {t('poe.ignore')}
            </button>
          </div>
        ))}
      {(step === 'approving' || step === 'waiting_ip') && <p className="small">{t('poe.waiting_ip')}</p>}
      {step === 'got_ip' && ip && (
        <div className="stack">
          <p className="small">{t('poe.got_ip', { ip })}</p>
          {own ? (
            <p className="small">{t('poe.own_camera')}</p>
          ) : (
            <div className="toolbar wrap">
              <input className="input" aria-label={t('onvif.user')} value={user} onChange={(e) => setUser(e.target.value)} data-testid="poe-user" />
              <input className="input" type="password" aria-label={t('auth.password')} placeholder={t('auth.password')} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="poe-password" />
              <button type="button" className="btn btn-primary" disabled={adding} onClick={() => void add()} data-testid="poe-add">
                {t('poe.add_channel')}
              </button>
            </div>
          )}
        </div>
      )}
      {step === 'added' && <p className="small ok">{t('poe.added')}</p>}
      {step === 'failed' && (
        <div className="toolbar">
          <button type="button" className="btn" onClick={() => void waitForIp()} data-testid="poe-retry-wait">
            {t('poe.wait_again')}
          </button>
        </div>
      )}
      {err && (
        <p className="error small" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}

/** 通道頁頂部：這台 NVR / Station 的待核准 PoE 攝影機（沒有 kinio-dhcpd、沒有管理權 → 不出現）。 */
export function PoeApprovals({ dev, canManage, initialOpen = false }: { dev: VmsDevice; canManage: boolean; initialOpen?: boolean }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<PendingItem[]>([]);
  const [supported, setSupported] = useState(true);
  const [open, setOpen] = useState(initialOpen);
  const [checked, setChecked] = useState(false);
  const active = useRef(new Set<string>());
  const [tick, setTick] = useState(0);
  const shown = canManage && !dev.parentAid && dev.presence !== 'offline';
  const enabled = shown && open && supported;
  const { apiEndpoint, homeId, aid } = dev;
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const target = { apiEndpoint, homeId, aid };
    const poll = async () => {
      try {
        const r = await gatewayRpc<{ items?: PendingItem[] }>(target, 'dhcp.pending', {});
        if (alive) setChecked(true);
        if (alive)
          setItems((prev) => {
            const all = r?.items ?? [];
            const keep = all.filter((x) => x.state === 'pending' || x.state === undefined || active.current.has(x.mac));
            // 正在走流程的卡（已核准）即使清單暫時沒回也留著
            for (const p of prev) if (active.current.has(p.mac) && !keep.some((x) => x.mac === p.mac)) keep.push(p);
            return keep;
          });
      } catch (e) {
        if (alive && e instanceof KinioError && NO_METHOD.has(e.code)) setSupported(false); // 這台沒有 kinio-dhcpd：不再問
      }
    };
    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled, apiEndpoint, homeId, aid, tick]); // 不依 dev 物件：設備清單每次重抓都換參照，會一直重設輪詢
  if (!shown) return null;
  return (
    <details className="card poe-block" data-testid="poe-approvals-block" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary data-testid="poe-approvals-toggle">{t('poe.block_title')}</summary>
      {!supported ? (
        <p className="muted small" data-testid="poe-unsupported">
          {t('poe.unsupported')}
        </p>
      ) : items.length === 0 ? (
        <p className="muted small" data-testid="poe-none">
          {checked ? t('poe.none') : t('common.loading')}
        </p>
      ) : (
        <section className="stack" data-testid="poe-approvals">
          <h2 className="section-title">{t('poe.title', { n: items.length })}</h2>
          {items.map((it) => (
            <Card
              key={it.mac}
              dev={dev}
              item={it}
              onChanged={() => setTick((n) => n + 1)}
              onActive={(on) => (on ? active.current.add(it.mac) : active.current.delete(it.mac))}
            />
          ))}
        </section>
      )}
    </details>
  );
}
